// Amplify
import {
  confirmSignIn,
  confirmResetPassword as amplifyConfirmResetPassword,
  fetchAuthSession,
  resetPassword as amplifyResetPassword,
  signIn as amplifySignIn,
  signInWithRedirect,
  signOut as amplifySignOut,
} from "aws-amplify/auth";

// Libraries
import i18next from "i18next";

// API
import { validateMbsSsoDomain } from "API/Resource/mbsSsoValidateDomain";

// Auth
import {
  hasValidMBSAmplifyConfig,
  hasValidMBSSsoConfig,
} from "./mbsAuthConfig";
import { clearAppSessionStorage } from "Auth/authUtils";
import { getActiveAuthDomain, setActiveAuthDomain } from "Auth/AuthDomain";
import {
  mbsStorage,
  clearPendingAuthMessage,
  setPendingAuthMessage,
} from "./mbsStorage";

// Constants
import {
  AUTH_DOMAINS,
  EXPIRED_CHALLENGE_SESSION_ERROR,
  MBS_AUTH_ERROR_CODES,
  MBS_SSO_EXPECTED_EMAIL_KEY,
} from "Constants/AuthConstants";

/**
 * Helper function to ensure MBS Amplify configuration is valid before attempting any auth operations.
 * Throws an error with a user-friendly message if the configuration is invalid.
 */
const assertValidMBSConfig = () => {
  if (!hasValidMBSAmplifyConfig()) {
    throw new Error(i18next.t("mbsAuth.errors.notConfigured"));
  }
};

const assertValidMBSSsoConfig = () => {
  if (!hasValidMBSSsoConfig()) {
    throw new Error(i18next.t("mbsAuth.errors.ssoNotConfigured"));
  }
};

const signOutMbsSession = async () => {
  try {
    await amplifySignOut();
  } catch {
    // Ignore Amplify sign-out errors and still clear local MBS session.
  } finally {
    clearAppSessionStorage();
  }
};

/**
 * MBS Auth implementation for sign-in, new-password confirmation, profile retrieval, and sign-out.
 */
export const mbsAuth = {
  signInWithSso: async (userEmail) => {
    assertValidMBSSsoConfig();
    clearPendingAuthMessage();

    const normalizedEmail = userEmail.trim().toLowerCase();
    const [, idpIdentifier = ""] = normalizedEmail.split("@");
    if (!idpIdentifier) {
      throw new Error(i18next.t("mbsAuth.errors.invalidSsoEmail"));
    }

    const { isValid } = await validateMbsSsoDomain(idpIdentifier);
    if (!isValid) {
      throw new Error(i18next.t("mbsAuth.errors.invalidSsoDomain"));
    }

    clearAppSessionStorage();
    mbsStorage.setItem(MBS_SSO_EXPECTED_EMAIL_KEY, normalizedEmail);

    await signInWithRedirect({
      provider: { idpIdentifier },
      options: {
        loginHint: normalizedEmail,
      },
    });

    return { challengeName: null };
  },

  signIn: async (userEmail, password) => {
    assertValidMBSConfig();
    clearPendingAuthMessage();
    clearAppSessionStorage();

    const normalizedEmail = userEmail.trim().toLowerCase();

    const signInResult = await amplifySignIn({
      username: normalizedEmail,
      password,
    });

    const signInStep = signInResult?.nextStep?.signInStep;
    if (signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED") {
      return {
        challengeName: "NEW_PASSWORD_REQUIRED",
        missingAttributes: signInResult?.nextStep?.missingAttributes || [],
      };
    }
    if (signInStep === "RESET_PASSWORD") {
      return {
        challengeName: "RESET_PASSWORD",
      };
    }

    if (signInStep === "DONE" || signInResult?.isSignedIn) {
      mbsStorage.removeItem(MBS_SSO_EXPECTED_EMAIL_KEY);
      setActiveAuthDomain(AUTH_DOMAINS.MBS);
      return { challengeName: null };
    }

    throw new Error(i18next.t("mbsAuth.errors.unexpectedAuthState"));
  },

  confirmNewPassword: async (newPassword, userAttributes = {}) => {
    assertValidMBSConfig();
    const confirmSignInInput = { challengeResponse: newPassword };
    if (Object.keys(userAttributes).length) {
      confirmSignInInput.options = { userAttributes };
    }

    let result;
    try {
      result = await confirmSignIn(confirmSignInInput);
    } catch (error) {
      if (error?.message?.includes(EXPIRED_CHALLENGE_SESSION_ERROR)) {
        const expiredSessionError = new Error(
          i18next.t("mbsAuth.errors.expiredChallengeSession")
        );
        expiredSessionError.code =
          MBS_AUTH_ERROR_CODES.EXPIRED_CHALLENGE_SESSION;
        throw expiredSessionError;
      }
      throw error;
    }

    if (result?.nextStep?.signInStep === "DONE" || result?.isSignedIn) {
      mbsStorage.removeItem(MBS_SSO_EXPECTED_EMAIL_KEY);
      setActiveAuthDomain(AUTH_DOMAINS.MBS);
      return { challengeName: null };
    }

    throw new Error(i18next.t("mbsAuth.errors.unexpectedAuthState"));
  },

  forgotPassword: async (userEmail) => {
    assertValidMBSConfig();
    clearPendingAuthMessage();

    let result;
    try {
      const normalizedEmail = userEmail.trim().toLowerCase();
      result = await amplifyResetPassword({ username: normalizedEmail });
    } catch (error) {
      if (error?.name === "UserNotFoundException") {
        throw new Error(i18next.t("errors.userNotFoundReset"));
      }
      throw error;
    }

    const resetPasswordStep = result?.nextStep?.resetPasswordStep;

    if (resetPasswordStep === "CONFIRM_RESET_PASSWORD_WITH_CODE") {
      return { nextStep: resetPasswordStep };
    }

    throw new Error(i18next.t("mbsAuth.errors.unexpectedAuthState"));
  },

  confirmForgotPassword: async (userEmail, code, newPassword) => {
    assertValidMBSConfig();

    const normalizedEmail = userEmail.trim().toLowerCase();
    await amplifyConfirmResetPassword({
      username: normalizedEmail,
      confirmationCode: code,
      newPassword,
    });
  },

  getCurrentUserProfile: async () => {
    const activeDomain = getActiveAuthDomain();
    if (activeDomain && activeDomain !== AUTH_DOMAINS.MBS) {
      return null;
    }
    if (!hasValidMBSAmplifyConfig()) {
      return null;
    }
    try {
      const session = await fetchAuthSession();
      const payload = session?.tokens?.idToken?.payload || null;
      if (!payload) return null;

      const expectedSsoEmail = mbsStorage.getItem(MBS_SSO_EXPECTED_EMAIL_KEY);
      const actualEmail = String(payload.email || "")
        .trim()
        .toLowerCase();

      if (expectedSsoEmail && expectedSsoEmail !== actualEmail) {
        const mismatchMessage = i18next.t("mbsAuth.errors.ssoEmailMismatch", {
          actualEmail: actualEmail || "unknown",
          expectedEmail: expectedSsoEmail,
        });

        setPendingAuthMessage(mismatchMessage, "error");
        await signOutMbsSession();
        return null;
      }

      mbsStorage.removeItem(MBS_SSO_EXPECTED_EMAIL_KEY);
      setActiveAuthDomain(AUTH_DOMAINS.MBS);
      return {
        email: actualEmail,
        firstName: payload.given_name || "",
        lastName: payload.family_name || "",
      };
    } catch {
      return null;
    }
  },

  signOut: async () => {
    await signOutMbsSession();
  },
};
